/**
 * Filtering and sorting without a page reload.
 *
 * The facet form is the single source of truth: its serialised state becomes the
 * URL, the URL is fetched through the Section Rendering API, and the returned
 * markup replaces the results region. Because the server renders it, filter counts
 * and disabled states stay accurate, and no state is duplicated in JS.
 *
 * The URL is pushed to history on every change, so filtered views are shareable and
 * the Back button steps through refinements.
 */
/* Wrapped in an IIFE so its top-level names stay out of the global scope.
   Theme scripts share one global namespace: when two of them declared the same
   const, the second failed to parse outright and silently did nothing. */
(() => {
  const RESULTS = '[data-collection-results]';
  const DRAWER_FACETS = '#facets-drawer [data-facets]';
  const DEBOUNCE_MS = 400;

  class CollectionFacets {
    constructor(root) {
      this.root = root;
      this.timer = null;
      this.controller = null;

      // Wrapped rather than passed directly: these handlers are private class fields,
      // which are initialised in declaration order — the ones declared below this
      // constructor are still undefined while it runs, so registering them here by
      // reference would silently bind nothing.
      document.addEventListener('change', (event) => this.#onChange(event));
      document.addEventListener('submit', (event) => this.#onSubmit(event));
      document.addEventListener('click', (event) => this.#onClick(event));
      window.addEventListener('popstate', () => this.#render(window.location.href, false));
    }

    #onSubmit = (event) => {
      const form = event.target.closest('[data-facets]');
      if (!form) return;
      event.preventDefault();
      clearTimeout(this.timer);
      this.#submit(form);
      // The drawer covers the results, so applying a filter means dismissing it.
      document.getElementById('facets-drawer')?.close();
    };

    #onChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      // Read from the form that actually changed. The sidebar and the mobile drawer
      // each render a copy, and only one is visible at a time, so grabbing "the first
      // one on the page" would submit whichever copy is currently hidden.
      const form = target.closest('[data-facets]');
      if (!form) return;

      // The "show all" toggle lives in the form but carries no name and filters
      // nothing — reacting to it would fire a pointless request on every expand.
      if (!target.name) return;

      const isText = target.type === 'number';
      clearTimeout(this.timer);
        // Typing a price should settle before firing; a checkbox is a finished decision.
      this.timer = setTimeout(() => this.#submit(form), isText ? DEBOUNCE_MS : 0);
    };

    #onClick = (event) => {
      // "Show more" points at the next page like any other link; it is the only one
      // that adds to the results instead of replacing them.
      const more = event.target.closest('[data-load-more]');
      if (more?.href) {
        event.preventDefault();
        this.#loadMore(more);
        return;
      }

      // Filter pills, "clear all" and pagination all resolve to a plain URL.
      const link = event.target.closest(
        '.facets__pill, .facets__clear, [data-collection-results] .pagination__item'
      );
      if (!link || !link.href) return;
      event.preventDefault();
      this.#render(link.href);
    };

    #submit(form) {
      if (!form) return;

      const params = new URLSearchParams(new FormData(form));
      // Empty price boxes would otherwise land in the URL as blank parameters.
      for (const [key, value] of [...params]) {
        if (value === '') params.delete(key);
      }

      const url = `${window.location.pathname}?${params}`;
      this.#render(url);
    }

    /* The mobile drawer sits outside the swapped region — replacing it while it is
       open would close it mid-interaction — so the numbers inside it are updated in
       place instead. Values only: the inputs themselves are left alone, so the tick
       that started this request, the focus and any collapsed groups all survive.

       Without this the drawer covers the results it is filtering and reports the
       count from before the first tick, which is the one number that tells you
       whether to keep narrowing or to go and look. */
    #syncDrawer(doc) {
      const live = document.querySelector(DRAWER_FACETS);
      const incoming = doc.querySelector(DRAWER_FACETS);
      if (!live || !incoming) return;

      // The head (result count, "clear all") and the active-filter pills hold no
      // input state, so they are swapped outright rather than patched.
      for (const selector of ['.facets__head', '.facets__active']) {
        const target = live.querySelector(selector);
        const source = incoming.querySelector(selector);

        if (target && source) target.replaceWith(source);
        else if (target) target.remove();
        else if (source) live.querySelector('.facets__head')?.after(source);
      }

      const submit = live.querySelector('[data-facets-submit]');
      const incomingSubmit = incoming.querySelector('[data-facets-submit]');
      if (submit && incomingSubmit) submit.textContent = incomingSubmit.textContent;

      // Per-value counts and availability, patched in place so the tick that started
      // this request keeps its focus.
      live.querySelectorAll('input[type="checkbox"][name]').forEach((input) => {
        const source = incoming.querySelector(
          `input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`
        );
        if (!source) return;

        input.disabled = source.disabled;
        const label = input.closest('.facet__option');
        label?.classList.toggle('facet__option--empty', source.disabled);

        const count = label?.querySelector('.facet__option-count');
        const incomingCount = source
          .closest('.facet__option')
          ?.querySelector('.facet__option-count');
        if (count && incomingCount) count.textContent = incomingCount.textContent;
      });
    }

    /* Appends the next page to the grid. The URL is deliberately left alone: it
       still describes page one of this filter, which is what a reload or a shared
       link should give back — pushing ?page=2 would hand someone a page that opens
       midway through the collection with the earlier products missing. */
    async #loadMore(button) {
      const grid = document.querySelector(`${RESULTS} .product-grid`);
      const wrap = button.closest('[data-load-more-wrap]');
      if (!grid || !wrap) return;

      const url = button.href;
      const returnFocus = document.activeElement === button;
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector(`${RESULTS} .product-grid`);
        if (!incoming) {
          console.warn('[facets] response had no grid; navigating');
          window.location.href = url;
          return;
        }

        grid.append(...incoming.children);

        // The fetched page carries its own button, aimed one page further on; the
        // last page carries none, which is what ends the sequence.
        const next = doc.querySelector('[data-load-more-wrap]');
        if (next) {
          wrap.replaceWith(next);
          // Keyboard users were standing on this control; leave them on it rather
          // than dropping focus to the top of the document. preventScroll is the
          // whole point here: the new button sits below the products just added, so
          // a plain focus() scrolls the page past all of them and the click looks
          // like it did nothing but jump to the bottom.
          if (returnFocus) {
            next.querySelector('[data-load-more]')?.focus({ preventScroll: true });
          }
        } else {
          wrap.remove();
        }
      } catch (error) {
        window.location.href = url;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    async #render(url, push = true) {
      const results = document.querySelector(RESULTS);
      if (!results) {
        console.warn('[facets] results container missing; falling back');
        window.location.href = url;
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      results.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector(RESULTS);
        if (!incoming) {
          // The response did not contain the region — better to show the real page
          // than to leave the old results in place looking like nothing happened.
          console.warn('[facets] response had no results region; navigating');
          window.location.href = url;
          return;
        }
        results.replaceWith(incoming);
        this.#syncDrawer(doc);

        if (push) window.history.pushState({}, '', url);
        // Land at the top of the new results rather than mid-grid.
        document.querySelector('.collection__head')?.scrollIntoView({ block: 'start' });
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = url;
      } finally {
        document.querySelector(RESULTS)?.removeAttribute('aria-busy');
      }
    }

  }

  const root = document.querySelector(RESULTS);
  if (root) {
    new CollectionFacets(root);
  } else {
    console.warn('[facets] no results container found — filtering will fall back to full page loads');
  }
})();
